import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  Paper
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface RecebimentoData {
  id: number;
  cliente_nome?: string;  // Tornado opcional para compatibilidade
  cliente_email?: string;
  cliente_telefone?: string;
  cliente_cpf?: string;
  descricao?: string;  // Campo do frontend
  detalhes?: string;   // Campo do backend
  valor: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: string;
  forma_pagamento?: string;
}

interface ReciboAtendimentoProps {
  open: boolean;
  onClose: () => void;
  recebimento: RecebimentoData | null;
}

const ReciboAtendimento: React.FC<ReciboAtendimentoProps> = ({
  open,
  onClose,
  recebimento
}) => {
  const { user } = useAuth();
  const [dadosUsuario, setDadosUsuario] = React.useState<any>(null);

  React.useEffect(() => {
    if (open && user?.id) {
      buscarDadosUsuario();
    }
  }, [open, user?.id]);

  const buscarDadosUsuario = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`http://localhost:8080/api/usuarios/${user?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setDadosUsuario(data.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
    }
  };

  if (!recebimento) return null;

  const formatarData = (data: string) => {
    try {
      const date = new Date(data);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };

  const formatarDataHora = (data: Date) => {
    return data.toLocaleDateString('pt-BR') + ' às ' + data.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const valorPorExtenso = (valor: number): string => {
    const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
    const milhares = ['', 'mil', 'dois mil', 'três mil', 'quatro mil', 'cinco mil', 'seis mil', 'sete mil', 'oito mil', 'nove mil'];
    
    if (valor === 0) return 'zero reais';
    if (valor === 100) return 'cem reais';
    
    const reais = Math.floor(valor);
    const centavos = Math.round((valor - reais) * 100);
    
    const converterNumero = (num: number): string => {
      if (num === 0) return '';
      if (num === 1) return 'um';
      if (num < 10) return unidades[num];
      if (num < 20) return especiais[num - 10];
      if (num < 100) {
        const dez = Math.floor(num / 10);
        const uni = num % 10;
        return dezenas[dez] + (uni > 0 ? ' e ' + unidades[uni] : '');
      }
      if (num < 1000) {
        const cen = Math.floor(num / 100);
        const resto = num % 100;
        return centenas[cen] + (resto > 0 ? ' e ' + converterNumero(resto) : '');
      }
      if (num < 1000000) {
        const mil = Math.floor(num / 1000);
        const resto = num % 1000;
        let resultado = '';
        
        if (mil === 1) {
          resultado = 'mil';
        } else if (mil < 10) {
          resultado = unidades[mil] + ' mil';
        } else if (mil < 20) {
          resultado = especiais[mil - 10] + ' mil';
        } else if (mil < 100) {
          const dezMil = Math.floor(mil / 10);
          const uniMil = mil % 10;
          resultado = dezenas[dezMil] + (uniMil > 0 ? ' e ' + unidades[uniMil] : '') + ' mil';
        } else {
          const cenMil = Math.floor(mil / 100);
          const restoMil = mil % 100;
          resultado = centenas[cenMil] + (restoMil > 0 ? ' e ' + converterNumero(restoMil) : '') + ' mil';
        }
        
        if (resto > 0) {
          resultado += ' e ' + converterNumero(resto);
        }
        return resultado;
      }
      return num.toString();
    };
    
    let resultado = '';
    
    if (reais > 0) {
      const reaisExtenso = converterNumero(reais);
      resultado += reaisExtenso.charAt(0).toUpperCase() + reaisExtenso.slice(1);
      resultado += reais === 1 ? ' Real' : ' Reais';
    }
    
    if (centavos > 0) {
      if (reais > 0) resultado += ' e ';
      const centavosExtenso = converterNumero(centavos);
      if (reais === 0) resultado += centavosExtenso.charAt(0).toUpperCase() + centavosExtenso.slice(1);
      else resultado += centavosExtenso;
      resultado += centavos === 1 ? ' Centavo' : ' Centavos';
    }
    
    return resultado;
  };

  const handleImprimir = () => {
    window.print();
  };

  // Garantir que temos um nome do cliente
  const nomeCliente = recebimento.cliente_nome || 'Cliente não informado';

  // Usar descricao ou detalhes, dependendo do que estiver disponível
  const descricaoAtendimento = recebimento.descricao || recebimento.detalhes || 'Atendimento psicológico';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogContent>
        <Paper 
          id="recibo-conteudo"
          elevation={0}
          sx={{ p: 3, border: '1px solid #e0e0e0' }}
        >
          {/* Header */}
          <Box className="header" sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h5" 
              className="titulo"
              sx={{ 
                fontWeight: 'bold',
                mb: 1,
                fontSize: '1.5rem'
              }}
            >
              RECIBO DE ATENDIMENTO PSICOLÓGICO
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recibo Nº {String(recebimento.id).padStart(6, '0')}/{new Date(recebimento.data_vencimento).getFullYear()}
            </Typography>
          </Box>

          {/* Informações do Cliente */}
          <Box className="info-section" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dados do Cliente
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  <span className="info-label">Nome:</span>
                  <strong>{nomeCliente}</strong>
                </Typography>
              </Grid>
              {recebimento.cliente_email && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ lineHeight: 1.3, mt: '-13px' }}>
                    <span className="info-label">E-mail:</span>
                    {recebimento.cliente_email}
                  </Typography>
                </Grid>
              )}
              {recebimento.cliente_cpf && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ lineHeight: 1.3, mt: '-13px' }}>
                    <span className="info-label">CPF:</span>
                    {recebimento.cliente_cpf}
                  </Typography>
                </Grid>
              )}
              {recebimento.cliente_telefone && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ lineHeight: 1.3 }}>
                    <span className="info-label">Telefone:</span>
                    {recebimento.cliente_telefone}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Informações do Atendimento */}
          <Box className="info-section" sx={{ mb: 3, mt: '-10px' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dados do Atendimento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  <span className="info-label">Descrição:</span>
                  {descricaoAtendimento}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ mt: '-10px' }}>
                <Typography>
                  <span className="info-label">Data de Vencimento:</span>
                  {formatarData(recebimento.data_vencimento)}
                </Typography>
              </Grid>
              {recebimento.data_pagamento && (
                <Grid item xs={12} md={6} sx={{ mt: '-10px' }}>
                  <Typography>
                    <span className="info-label">Data de Pagamento:</span>
                    {formatarData(recebimento.data_pagamento)}
                  </Typography>
                </Grid>
              )}
              {recebimento.forma_pagamento && (
                <Grid item xs={12} md={6} sx={{ mt: '-10px' }}>
                  <Typography>
                    <span className="info-label">Forma de Pagamento:</span>
                    {recebimento.forma_pagamento}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Valor */}
          <Box 
            className="valor-destaque"
            sx={{ 
              textAlign: 'center',
              p: 2,
              border: 2,
              borderColor: 'primary.main',
              borderRadius: 1,
              my: 3
            }}
          >
            <Typography variant="h5" color="primary" fontWeight="bold">
              Valor Total: {formatarMoeda(recebimento.valor)}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: 'italic', 
                color: '#666666',
                mt: 0.5,
                fontSize: '0.75rem'
              }}
            >
              ({valorPorExtenso(recebimento.valor)})
            </Typography>
          </Box>

          {/* Declaração */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
              Declaro que recebi do(a) Sr(a). <strong>{nomeCliente}</strong> a 
              quantia de <strong>{formatarMoeda(recebimento.valor)}</strong> referente aos 
              serviços de atendimento psicológico prestados conforme descrição acima.
            </Typography>
          </Box>

          {/* Assinatura */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            {dadosUsuario?.assinatura && (
              <Box sx={{ mb: 1 }}>
                <img 
                  src={dadosUsuario.assinatura.startsWith('data:') 
                    ? dadosUsuario.assinatura 
                    : `data:image/png;base64,${dadosUsuario.assinatura}`}
                  alt="Assinatura"
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '60px',
                    objectFit: 'contain',
                    border: '1px solid #ccc'
                  }}
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', e);
                    console.log('SRC da imagem:', e.currentTarget.src);
                  }}
                  onLoad={() => {
                    console.log('Imagem carregada com sucesso!');
                  }}
                />
              </Box>
            )}
            <Box sx={{ 
              borderTop: '1px solid #333', 
              width: '300px', 
              mx: 'auto', 
              mt: dadosUsuario?.assinatura ? 2 : 8, 
              pt: 1 
            }}>
              <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2, mb: '3px' }}>
                {dadosUsuario?.user_name || user?.user_name || 'Nome do Usuário'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, mb: '3px', display: 'block' }}>
                CPF: {dadosUsuario?.cpf || 'Não informado'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
                CRP: {dadosUsuario?.crp || 'Não informado'}
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box 
            className="footer"
            sx={{ 
              mt: 4, 
              pt: 2, 
              borderTop: '1px solid #ddd',
              textAlign: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Documento gerado em {formatarDataHora(new Date())} pelo sistema Trajetus
            </Typography>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, '@media print': { display: 'none' } }}>
        <Button onClick={onClose} color="inherit">
          Fechar
        </Button>
        <Button 
          onClick={handleImprimir} 
          variant="contained"
          color="primary"
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReciboAtendimento;
